/**
 * Shared, non-component helpers for the Workshop Log controls. Kept apart
 * from `log-controls.tsx` so that file exports only components (keeps
 * React Fast Refresh happy) while the modal and the controls share one
 * source for field chrome and date formatting.
 */

import { cn } from "@/lib/utils";

/** Shared compact field chrome — the text inputs, the status trigger,
 *  and the date trigger all wear it, so every control in a row lines up. */
export const FIELD_CLASS = cn(
  "border-border/60 bg-background/60 text-foreground placeholder:text-muted-foreground/60",
  "focus-visible:ring-ring w-full rounded-md border px-3 py-2 text-sm",
  "focus-visible:ring-2 focus-visible:outline-none transition",
);

/** Parse a stored `YYYY-MM-DD` into a local-midnight Date, or null. */
export function parseISO(value: string): Date | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  // The regex above guarantees exactly three numeric parts, so the tuple
  // cast is sound — it just tells `noUncheckedIndexedAccess` what we know.
  const [y, m, d] = value.split("-").map(Number) as [number, number, number];
  const dt = new Date(y, m - 1, d);
  return Number.isNaN(dt.getTime()) ? null : dt;
}

export function toISO(y: number, m: number, d: number): string {
  return `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
}

export function sameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/** Number of days in a month. `month` is 0-indexed (0 = January). */
export function daysInMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate();
}

/** Leading blank cells before day 1 in a Monday-first calendar grid
 *  (Mon = 0 … Sun = 6). `month` is 0-indexed. Extracted from the
 *  DueDatePicker so the off-by-one-prone weekday math can be unit-tested
 *  across month/year boundaries. */
export function leadingBlanks(year: number, month: number): number {
  return (new Date(year, month, 1).getDay() + 6) % 7;
}

/**
 * Status → accent colour, shared across every register (Decisions, Actions,
 * Assumptions, Findings, Opportunities, Recommendations). Colour carries
 * meaning (the console principle): settled/positive terminal states read
 * green, in-flight states sky, negated/parked states melon, and fresh/new
 * states stay neutral. The register status sets rhyme, so one table covers
 * them all.
 */
export function statusAccent(status: string): string {
  switch (status) {
    case "agreed":
    case "validated":
    case "done":
    case "actioned":
    case "accepted":
    case "pursued":
      return "var(--accent-green)";
    case "in-progress":
    case "noted":
    case "proposed":
    case "exploring":
      return "var(--accent-sky)";
    case "parked":
    case "refuted":
    case "rejected":
    case "discarded":
      return "var(--accent-melon)";
    default:
      // fresh/new states: open, draft, identified, assumed, proposed(decision)
      return "var(--text-secondary)";
  }
}

/** Finding severity → accent. high reads red, medium gold, low sky, info
 *  muted — so the severity chip carries its weight at a glance. */
export function severityAccent(severity: string): string {
  switch (severity) {
    case "high":
      return "var(--accent-red)";
    case "medium":
      return "var(--accent-gold)";
    case "low":
      return "var(--accent-sky)";
    default:
      return "var(--text-muted)";
  }
}

/** Render a stored `YYYY-MM-DD` as `DD Mon YYYY`. Legacy free-text values
 *  (pre-picker) are shown verbatim. */
export function formatDueDate(due: string): string {
  const d = parseISO(due);
  if (!d) return due;
  return d.toLocaleDateString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}
